/**
 * @nanokart/tryon-studio-sdk
 * useTryOn hook - Execute virtual try-on
 */

import { useCallback } from 'react';
import { useStore } from './useStore';
import type { TryOnResult, CatalogItem } from '../../types/config';

export interface UseTryOnReturn {
  /** Current try-on result */
  currentResult: TryOnResult | null;
  /** Whether try-on is being generated */
  isGenerating: boolean;
  /** Error message if any */
  error: string | null;
  /** All cached results */
  results: Record<string, TryOnResult>;
  /** Execute try-on for a product */
  executeTryOn: (productId: string) => Promise<TryOnResult>;
  /** Get cached result for a product */
  getCachedResult: (productId: string) => TryOnResult | null;
  /** Clear error */
  clearError: () => void;
  /** Check if product has been tried on */
  hasTriedOn: (productId: string) => boolean;
}

/**
 * Hook for executing virtual try-on
 * 
 * @example
 * ```tsx
 * const { executeTryOn, isGenerating, currentResult } = useTryOn();
 * 
 * const handleTryOn = async () => {
 *   await executeTryOn(selectedProduct.productId);
 * };
 * ```
 */
export function useTryOn(): UseTryOnReturn {
  const currentResult = useStore((s) => s.tryon.currentResult);
  const isGenerating = useStore((s) => s.tryon.isGenerating);
  const error = useStore((s) => s.tryon.error);
  const results = useStore((s) => s.tryon.results);
  
  const executeTryOnAction = useStore((s) => s.executeTryOn);
  const getCachedResultAction = useStore((s) => s.getCachedResult);
  const clearTryOnError = useStore((s) => s.clearTryOnError);

  const executeTryOn = useCallback(
    async (productId: string) => {
      return executeTryOnAction(productId);
    },
    [executeTryOnAction]
  );

  const getCachedResult = useCallback(
    (productId: string) => {
      return getCachedResultAction(productId);
    },
    [getCachedResultAction]
  );

  const clearError = useCallback(() => {
    clearTryOnError();
  }, [clearTryOnError]);

  const hasTriedOn = useCallback(
    (productId: string) => {
      return productId in results;
    },
    [results]
  );

  return {
    currentResult,
    isGenerating,
    error,
    results,
    executeTryOn,
    getCachedResult,
    clearError,
    hasTriedOn,
  };
}
